import { arcadeApiClient } from '../apiClients'
import { v4 as randomUUID } from 'uuid'
import { useCallback, useEffect, useState } from 'react'
import useSWR from 'swr'
import { PersonalNotification } from 'ui/types/PersonalNotification'

const fetcher = async (url: string) =>
  await arcadeApiClient.get(url).then(res => {
    return res.data.personalNotifications as PersonalNotification[]
  })

export const usePersonalNotifications = ({
  userId,
  rows_per_page = 5,
}: {
  userId?: number
  rows_per_page?: number
}) => {
  const [lastId, setLastId] = useState<number>()
  const [more, setMore] = useState(true)
  const [personalNotifications, setPersonalNotifications] = useState<
    PersonalNotification[]
  >([])
  const [fingerPrint, setFingerPrint] = useState<number>(randomUUID())
  useEffect(() => {
    setPersonalNotifications([])
    setMore(true)
    setLastId(undefined)
    setFingerPrint(randomUUID())
  }, [userId])

  const query = new URLSearchParams()
  query.append('rows_per_page', rows_per_page.toString())
  lastId && query.append('last_id', lastId.toString())

  const { data: notifications, isValidating: loading } = useSWR(
    userId ? `/multi_platform/users/${userId}/personal_notifications?${query.toString()}#${fingerPrint}` : null,
    fetcher,
  )

  useEffect(() => {
    if (!notifications) return
    setPersonalNotifications(items => {
      const ids = items.map(item => item.id)
      const newItems = notifications.filter(item => !ids.includes(item.id))
      return [...items, ...newItems]
    })
  }, [notifications])

  const loadMore = useCallback(() => {
    if (!notifications) return
    setLastId(personalNotifications[personalNotifications.length - 1]?.id)
    if (notifications.length < rows_per_page) {
      setMore(false)
    }
  }, [personalNotifications, rows_per_page])

  const markNotificationAsRead = (id: number) => {
    arcadeApiClient
      .put(
        `/multi_platform/users/${userId}/personal_notifications/${id}/mark_as_read`,
      )
      .then(() => {
        setPersonalNotifications(
          personalNotifications.map(notification => {
            if (notification.id === id) {
              return { ...notification, readAt: new Date() }
            }
            return notification
          }),
        )
      })
      .catch(() => {
        console.error('Failed to mark notification as read')
      })
  }

  const markAllAsRead = () => {
    arcadeApiClient
      .put(
        `/multi_platform/users/${userId}/personal_notifications/mark_all_as_read`,
      )
      .then(() =>
        setPersonalNotifications(
          personalNotifications.map(notification => {
            return { ...notification, readAt: new Date() }
          }),
        ),
      )
      .catch(() => {
        console.error('Failed to mark all notifications as read')
      })
  }

  const pollingQuery = new URLSearchParams()
  pollingQuery.append('rows_per_page', rows_per_page.toString())
  const { data: firstPage } = useSWR(
    // Poll is appended to the end of this URL to distiguish it from the initial newsfeed request URL
    userId ? `/multi_platform/users/${userId}/personal_notifications?${pollingQuery.toString()}#${fingerPrint}-poll` : null,
    fetcher,
    {
      refreshInterval: 30000,
      isPaused: () => loading || !notifications,
    },
  )

  useEffect(() => {
    if (!firstPage) return
    setPersonalNotifications(items => {
      const ids = items.map(item => item.id)
      const newItems = firstPage.filter(item => !ids.includes(item.id))
      return [...newItems, ...items]
    })
  }, [firstPage])

  return {
    personalNotifications,
    loading,
    loadMore,
    hasMore: more,
    markNotificationAsRead,
    markAllAsRead,
  }
}
